import roster from "./data/roster.json";
import pq from "./data/pq.json";

// Real COMTRADE waveform fixtures, bundled eagerly at build time. Keyed by
// the glob path (e.g. "./data/waveforms/demo-pq-001.json"); waveformFor()
// resolves an event_id by matching the trailing "/{id}.json" segment.
const WAVEFORMS = import.meta.glob("./data/waveforms/*.json", { eager: true }) as Record<string, { default: unknown }>;
function waveformFor(id: string): unknown | null {
  const hit = Object.entries(WAVEFORMS).find(([k]) => k.endsWith(`/${id}.json`));
  return hit ? hit[1].default : null;
}

// Real (redacted) fastlog ½-cycle current series (channels Ia/Ib/Ic), bundled
// eagerly. EventDetail reads it from row.metadata.fastlog_series to draw the
// fastlog chart beside the COMTRADE waveform. Kept in separate files (not inline
// in pq.json) so the feed list stays lean — merged into the single-event response only.
const FASTLOGS = import.meta.glob("./data/fastlogs/*.json", { eager: true }) as Record<string, { default: unknown }>;
function fastlogFor(id: string): unknown | null {
  const hit = Object.entries(FASTLOGS).find(([k]) => k.endsWith(`/${id}.json`));
  return hit ? hit[1].default : null;
}

const CAPS = { context: "cloud", dyno_enabled: true, features: {},
  data_handling: { posture: "verified_default_api", verified_at: "2026-06-01T00:00:00Z",
    verified_by: "demo", anthropic_terms_reference_url: "https://www.anthropic.com/legal/commercial-terms" } };

export function jsonResponse(obj: unknown, status = 200): Response {
  return new Response(JSON.stringify(obj), { status, headers: { "Content-Type": "application/json" } });
}

function qp(path: string, key: string): string | null {
  const i = path.indexOf("?"); if (i < 0) return null;
  return new URLSearchParams(path.slice(i + 1)).get(key);
}

export function routeApi(path: string, method: string, _body?: unknown): Response {
  const p = path.split("?")[0];
  if (method !== "GET") return jsonResponse({ ok: true });   // writes: accepted no-op

  if (p === "/api/capabilities") return jsonResponse(CAPS);
  if (p === "/api/modems") return jsonResponse({ modems: [] });
  if (p === "/api/dyno/migrations") return jsonResponse({ migrations: [] });
  if (p === "/api/windows" || p.startsWith("/api/windows?")) return jsonResponse({ windows: [roster.window] });
  if (/^\/api\/windows\/[^/]+$/.test(p)) return jsonResponse(roster.window);
  if (p === "/api/services") return jsonResponse({ services: roster.services });
  if (p === "/api/venues") return jsonResponse({ venues: [roster.venue], count: 1 });
  if (/^\/api\/tours\/[^/]+$/.test(p)) return jsonResponse(roster.tour);
  if (p === "/api/touring-vocabulary") return jsonResponse({ identities: [] });

  if (/^\/api\/pq\/events\/[^/]+\/waveform$/.test(p)) {
    const id = p.split("/")[4];
    const wf = waveformFor(id);
    return wf ? jsonResponse(wf) : jsonResponse({ event_id: id, pending_forward: true, detail: "demo" });
  }
  if (/^\/api\/pq\/events\/[^/]+\/analysis$/.test(p)) {
    // HarmonicAnalysis: empty channels (no spectrum) + a null system carrying an
    // honest skipped-reason. Renders the IEC-61000-4-7 card as a clean note rather
    // than a spectrum — truthful: continuous PQ logging post-dates this show.
    const id = p.split("/")[4];
    return jsonResponse({
      event_id: id,
      analysis_version: "demo",
      event: { frequency_hz: 60, sample_rate_hz: 15360, n_channels: 6, n_samples: 2030, capture_duration_s: null, window_unsynchronized: null },
      channels: [],
      system: {
        v_thd_avg_pct: null, i_thd_avg_pct: null, v_unbalance_pct: null, peak_i_any_phase: null,
        time_to_peak_s: null, seq_v_pos: null, seq_v_neg: null, seq_v_zero: null,
        neutral_i_measured: null, neutral_i_calculated: null,
        system_aggregates_skipped_reason: "Harmonic spectrum not captured for this event (continuous PQ logging post-dates this show).",
      },
    });
  }
  if (/^\/api\/pq\/events\/[^/]+$/.test(p)) {
    const id = p.split("/")[4];
    const ev = pq.items.find((e: { event_id: string }) => e.event_id === id);
    if (!ev) return jsonResponse({});
    const fl = fastlogFor(id);
    if (!fl) return jsonResponse(ev);
    const meta = ((ev as Record<string, unknown>).metadata as Record<string, unknown>) ?? {};
    return jsonResponse({ ...ev, metadata: { ...meta, fastlog_series: fl } });
  }
  if (p === "/api/pq/events") {
    const limit = Number(qp(path, "limit") ?? 200), offset = Number(qp(path, "offset") ?? 0);
    const sid = qp(path, "system_id");
    const all = sid ? pq.items.filter((e: { system_id: string }) => e.system_id === sid) : pq.items;
    return jsonResponse({ items: all.slice(offset, offset + limit), total: all.length, limit, offset, window_id: roster.window.window_id });
  }

  // Admin surfaces. The roster.json keys exist (Task 9 fixtures), so direct
  // access typechecks via resolveJsonModule — no casts.
  if (p.startsWith("/api/windows/") && p.endsWith("/roster")) return jsonResponse(roster.rosterResponse);
  if (p.startsWith("/api/windows/") && p.endsWith("/house-feed-assignments"))
    return jsonResponse({ window_id: roster.window.window_id, assignments: roster.feedAssignments });
  if (p.startsWith("/api/windows/") && p.endsWith("/show-periods"))
    return jsonResponse({ window_id: roster.window.window_id, periods: roster.showPeriods });
  if (p.startsWith("/api/pq/window/") && p.endsWith("/event-summary")) return jsonResponse(roster.eventSummary);
  if (p === "/api/dyno/acuvim-roster") return jsonResponse({ systems: roster.acuvimRoster });
  if (p === "/api/feed-audit") return jsonResponse(roster.feedAudit);
  if (p === "/api/feed-suggestions") return jsonResponse(roster.feedSuggestions);
  if (p.startsWith("/api/venues/") && p.endsWith("/house-feeds"))
    return jsonResponse({ feeds: roster.houseFeeds, count: roster.houseFeeds.length });

  return jsonResponse({});   // unknown /api/* — 200, never 401
}
